/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        authStore.ts
 * @description Store Zustand pour la gestion de l'authentification utilisateur
 *              Gere le token JWT, les informations utilisateur et la persistance
 *
 * @features    - Authentification via JWT (Lexik JWT Authentication Bundle)
 *              - Persistance du token dans localStorage
 *              - Decodage automatique du payload JWT
 *              - Verification des roles utilisateur
 *
 * @roles       ROLE_ADMIN              - Administrateur systeme
 *              ROLE_GID_REGUL          - Regulateur GID (creer, valider, annuler)
 *              ROLE_CHEF_DENEIGNEMENT  - Chef d'equipe (affecter)
 *              ROLE_AGENT_DENEIGNEMENT - Agent terrain (demarrer, terminer, photos)
 *
 * @persistence Cle localStorage: 'falcon-auth'
 *              Stocke: { token, user }
 *
 * @version     2.1
 * @author      Equipe FALCON
 * =============================================================================
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginCredentials } from '@/types';
import apiClient from '@/services/api';

/**
 * Decode un token JWT (partie payload)
 *
 * Extrait et decode la partie payload (2eme segment) d'un token JWT
 * pour recuperer les informations utilisateur sans appel serveur.
 *
 * @param token - Le token JWT complet (header.payload.signature)
 * @returns L'objet decode contenant username et roles, ou null si invalide
 */
function decodeJwt(token: string): { username: string; roles: string[] } | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Resultat de la tentative de connexion
 */
export interface LoginResult {
  /** True si l'authentification 2FA est requise */
  requires2FA: boolean;
  /** Token temporaire pour la 2FA (si requires2FA est true) */
  tempToken?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  complete2FA: (tempToken: string, code: string) => Promise<void>;
  loadUserInfo: (token: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User | null) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (credentials: LoginCredentials): Promise<LoginResult> => {
        set({ isLoading: true });
        try {
          // Symfony attend "username" au lieu de "email"
          const response = await apiClient.post<{
            token?: string;
            requires2FA?: boolean;
            tempToken?: string;
          }>('/login', {
            username: credentials.email,
            password: credentials.password,
          });

          // Verifier si la 2FA est requise
          if (response.data.requires2FA && response.data.tempToken) {
            set({ isLoading: false });
            return {
              requires2FA: true,
              tempToken: response.data.tempToken,
            };
          }

          // Authentification reussie sans 2FA
          const { token } = response.data;
          if (!token) {
            throw new Error('Token manquant dans la reponse');
          }

          // Stocker le token temporairement pour que apiClient l'utilise
          set({ token });

          // Charger les infos utilisateur completes via /me
          await get().loadUserInfo(token);

          return { requires2FA: false };
        } catch (error) {
          set({ isLoading: false, token: null });
          throw error;
        }
      },

      /**
       * Complete l'authentification 2FA avec le code OTP
       */
      complete2FA: async (tempToken: string, code: string): Promise<void> => {
        set({ isLoading: true });
        try {
          const response = await apiClient.post<{ token: string }>('/login/2fa', {
            tempToken,
            code,
          });

          const { token } = response.data;

          // Stocker le token
          set({ token });

          // Charger les infos utilisateur completes via /me
          await get().loadUserInfo(token);
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      /**
       * Charge les informations utilisateur apres authentification
       */
      loadUserInfo: async (token: string): Promise<void> => {
        try {
          const meResponse = await apiClient.get<User>('/me');
          set({
            user: meResponse.data,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          // Fallback: decoder le token si /me echoue
          const decoded = decodeJwt(token);
          const user: User | null = decoded
            ? {
                id: 0,
                email: decoded.username,
                nom: decoded.username.split('@')[0],
                prenom: '',
                roles: decoded.roles,
                actif: true,
                nomComplet: decoded.username.split('@')[0],
              }
            : null;
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      setUser: (user: User | null) => {
        set({ user, isAuthenticated: !!user });
      },

      checkAuth: async () => {
        const token = get().token;
        if (!token) {
          set({ isAuthenticated: false });
          return;
        }

        try {
          const response = await apiClient.get<User>('/me');
          set({ user: response.data, isAuthenticated: true });
        } catch {
          set({ user: null, token: null, isAuthenticated: false });
        }
      },
    }),
    {
      name: 'falcon-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
      }),
    }
  )
);

/**
 * Helpers pour verifier les roles
 */
export const useHasRole = (role: string): boolean => {
  const user = useAuthStore((state) => state.user);
  return user?.roles.includes(role) ?? false;
};

export const useIsAdmin = (): boolean => useHasRole('ROLE_ADMIN');
export const useIsGidRegul = (): boolean => useHasRole('ROLE_GID_REGUL') || useIsAdmin();
export const useIsChefEquipe = (): boolean => useHasRole('ROLE_CHEF_DENEIGNEMENT') || useIsGidRegul();
export const useIsAgent = (): boolean => useHasRole('ROLE_AGENT_DENEIGNEMENT') || useIsChefEquipe();
